// src/admin/pages/AdminReferrals.jsx
// Platform-wide referral program tracking -- pulls GET /admin/referrals once
// per page load. Backed by platform_referral_summary(), which only reads the
// small `referrals` table, so this is cheap even as it grows.

import { useEffect, useState, useCallback } from "react";
import { Loader2, Gift, Users, CalendarClock } from "lucide-react";
import RequireAdmin from "@/admin/components/RequireAdmin";
import AdminLayout from "@/admin/components/AdminLayout";
import adminApi from "@/admin/lib/adminApi";

function StatCard({ label, value, tone = "bg-ink/5" }) {
  return (
    <div className={`rounded-2xl p-4 text-center ${tone}`}>
      <p className="text-xs uppercase tracking-[0.15em] text-ink/60">{label}</p>
      <p className="mt-1 font-display text-2xl font-black">{value}</p>
    </div>
  );
}

function AdminReferralsInner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/referrals");
    setData(data);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    load()
      .catch(() => setError("Couldn't load referral stats."))
      .finally(() => setLoading(false));
  }, [load]);

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-2">
          <Gift size={20} className="text-hotpink" />
          <h1 className="font-display text-3xl tracking-tighter">Referrals</h1>
        </div>
        <p className="mt-1 text-sm text-ink/50">
          What the referral program has cost in extra days, and who's driving it.
        </p>

        {loading ? (
          <div className="mt-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink/40" />
          </div>
        ) : error ? (
          <p className="mt-10 text-center text-sm text-ink/50">{error}</p>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard label="Days granted (30d)" value={`+${data.days_granted_last_30_days}`} tone="bg-mint/60" />
              <StatCard label="Days granted (lifetime)" value={`+${data.days_granted_total}`} tone="bg-peach/60" />
              <StatCard label="Referrals (30d)" value={data.qualified_last_30_days} />
              <StatCard label="Referrals (lifetime)" value={data.qualified_total} />
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/50">
              <span>{data.distinct_referrers} merchant{data.distinct_referrers === 1 ? "" : "s"} have earned a reward</span>
              <span>{data.pending_total} referral{data.pending_total === 1 ? "" : "s"} signed up, not yet paid</span>
              <span>{data.skipped_total} skipped (referrer's plan had lapsed)</span>
            </div>

            <div className="mt-8 rounded-3xl border border-ink/10 bg-white p-5">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Top referrers</p>
              </div>
              {data.top_referrers.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">No qualified referrals yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {data.top_referrers.map((r) => (
                    <div
                      key={r.business_id}
                      className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm"
                    >
                      <div>
                        <p className="font-semibold">
                          {r.name}
                          {r.ig_username && <span className="font-normal text-ink/50"> @{r.ig_username}</span>}
                        </p>
                        <p className="text-xs text-ink/50">{r.qualified_count} successful referral{r.qualified_count === 1 ? "" : "s"}</p>
                      </div>
                      <p className="font-semibold text-hotpink">+{r.days_earned}d</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 rounded-3xl border border-ink/10 bg-white p-5">
              <div className="flex items-center gap-2">
                <CalendarClock size={16} className="text-hotpink" />
                <p className="font-display text-lg font-black tracking-tight">Recent activity</p>
              </div>
              {data.recent.length === 0 ? (
                <p className="mt-3 text-sm text-ink/50">Nothing yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {data.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm">
                      <p>
                        <span className="font-semibold">{r.referrer_name}</span>
                        <span className="text-ink/40"> referred </span>
                        <span className="font-semibold">{r.referred_name}</span>
                      </p>
                      <div className="text-right">
                        <p className="font-semibold text-hotpink">+{r.reward_days}d</p>
                        <p className="text-xs text-ink/50">{new Date(r.qualified_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminReferrals() {
  return (
    <RequireAdmin>
      <AdminReferralsInner />
    </RequireAdmin>
  );
}
